// «Anterior / Siguiente» dentro de la ficha: recorre a los candidatos EN EL MISMO orden y con el mismo filtro
// que la vista de donde vino (Tablero, Lista o Tarjetas; pestaña; vacante; filtros y orden de la Lista).
// La vista arma la secuencia al abrir una ficha y la deja en sessionStorage (solo esta pestaña del navegador);
// si alguien abre la ficha por liga directa, la ficha la reconstruye con la vista recordada.
import { COLUMNAS, grupoDe, masRecientesPrimero, ordenColumna, type Grupo } from "@/reclutamiento/logica";
import type { CandidatoPortal, Columna, Etapa, Veredicto } from "@/reclutamiento/tipos";

export type Vista = "tablero" | "lista" | "tarjetas";
export type Pestana = Grupo | "todos";

/** Columnas del Tablero en cada pestaña: Algún día, Contratado y Descartados solo en la suya o en «Todos». */
export const COLUMNAS_DE: Record<Pestana, Columna[]> = {
  activos: ["cumple", "revisar", "contactado", "entrevistado"],
  algun_dia: ["algun_dia"],
  contratados: ["contratado"],
  descartados: ["descartado"],
  todos: COLUMNAS,
};
/** «Todos» va primero y es la de arranque (Andrés, 6-oct): la RH ve todas las etapas juntas; las demás son filtros. */
export const PESTANAS: [Pestana, string][] = [
  ["todos", "Todos"],
  ["activos", "Activos"],
  ["algun_dia", "Algún día"],
  ["contratados", "Contratados"],
  ["descartados", "Descartados"],
];
export const PESTANA_INICIAL: Pestana = "todos";
export const esPestana = (v: string | null): v is Pestana => !!v && PESTANAS.some(([p]) => p === v);
export const esVista = (v: string | null): v is Vista => v === "tablero" || v === "lista" || v === "tarjetas";
export const ETIQUETA_VISTA: Record<Vista, string> = { tablero: "Tablero", lista: "Lista", tarjetas: "Tarjetas" };
/** «Activos · Tablero» */
export const etiquetaVista = (p: Pestana, v: Vista) => `${PESTANAS.find(([x]) => x === p)![1]} · ${ETIQUETA_VISTA[v]}`;
/** Clave de la preferencia de pestaña (se recuerda por usuario, en este navegador). */
export const clavePestana = (s: { usuario: { correo: string } | null; cliente: { id: string } }) =>
  "pestana-" + (s.usuario?.correo ?? s.cliente.id);

/**
 * La pestaña recordada. Antes del 6-oct se arrancaba en «Activos» y casi todos la tienen guardada: la primera vez
 * tras el cambio se pasa a «Todos» UNA vez (queda una marca) y desde ahí se respeta lo que la persona elija.
 */
export function pestanaRecordada(
  clave: string,
  pref: { leer: (k: string) => string | null; guardar: (k: string, v: string) => void },
): Pestana {
  const marca = clave + "-todos-6oct";
  if (pref.leer(marca) !== "1") {
    pref.guardar(clave, PESTANA_INICIAL);
    pref.guardar(marca, "1");
    return PESTANA_INICIAL;
  }
  const p = pref.leer(clave);
  return esPestana(p) ? p : PESTANA_INICIAL;
}

export const enPestana = (k: CandidatoPortal, p: Pestana) => p === "todos" || grupoDe(k.columna) === p;

// ---------------------------------------------------------------- Filtros que llegan de Inicio (por la URL)
/** Las etapas que Inicio cuenta y enlaza: «Sin contactar», «Contactados», «Entrevistados». */
export type EtapaFiltro = "nuevo" | "contactado" | "entrevistado";
export const ETIQUETA_ETAPA_FILTRO: Record<EtapaFiltro, string> = {
  nuevo: "Sin contactar",
  contactado: "Contactados",
  entrevistado: "Entrevistados",
};
export const esEtapaFiltro = (v: string | null): v is EtapaFiltro => v === "nuevo" || v === "contactado" || v === "entrevistado";
/** Clave de la preferencia de plaza (por usuario, en este navegador; igual que la pestaña). */
export const clavePlaza = (s: { usuario: { correo: string } | null; cliente: { id: string } }) =>
  "plaza-" + (s.usuario?.correo ?? s.cliente.id);
/**
 * «Nuevo desde tu última visita», con la MISMA regla que el número de Inicio (`resumenVacante` / `portal_inicio`):
 * activo y entró a la base después de la visita anterior (sin visita anterior, todos los activos).
 */
export const esNuevoDesde = (k: CandidatoPortal, desde: string | null) =>
  grupoDe(k.columna) === "activos" && (!desde || Date.parse(k.ingresado_en ?? k.recibido_en) > Date.parse(desde));

// ---------------------------------------------------------------- Búsqueda por nombre o teléfono
/** Minúsculas y sin acentos: «JOSÉ» → «jose». */
export const normalizar = (t: string) =>
  t
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
/**
 * ¿`k` coincide con lo buscado? Por nombre (sin distinguir mayúsculas ni acentos, cada palabra en cualquier
 * orden) o, si lo buscado son solo dígitos (al menos 3), por los dígitos de su teléfono («8123» encuentra
 * «+52 81 1234 8123»). Vacío = todos.
 */
export function coincide(k: Pick<CandidatoPortal, "nombre" | "telefono">, q: string): boolean {
  const n = normalizar(q);
  if (!n) return true;
  const digitos = n.replace(/[\s()+.-]/g, "");
  if (/^\d{3,}$/.test(digitos)) return (k.telefono ?? "").replace(/\D/g, "").includes(digitos);
  const nombre = normalizar(k.nombre ?? "");
  return n.split(" ").every((p) => nombre.includes(p));
}

// ---------------------------------------------------------------- Lista: orden y filtros
export type Orden = "nombre" | "ver" | "fecha" | "ciudad" | "etapa";
export type EstadoLista = {
  orden: Orden;
  dir: 1 | -1;
  fVer: "" | Veredicto;
  fEta: "" | Etapa;
  fFec: "" | "3" | "7";
};
/** Por omisión: Fecha, los más recientes primero. */
export const LISTA_INICIAL: EstadoLista = { orden: "fecha", dir: -1, fVer: "", fEta: "", fFec: "" };
const ORD_VER: Record<Veredicto, number> = { cumple: 0, revisar: 1, descartado: 2 };

/** Las filas de la Lista, filtradas y ordenadas (la misma función la usa la ficha para Anterior/Siguiente). */
export function filasLista(items: CandidatoPortal[], e: EstadoLista, ahora = Date.now()): CandidatoPortal[] {
  const corte = e.fFec ? ahora - Number(e.fFec) * 86400000 : null;
  const clave: Record<Orden, (k: CandidatoPortal) => string | number> = {
    nombre: (k) => (k.nombre ?? "").toLowerCase(),
    ver: (k) => ORD_VER[k.veredicto],
    fecha: (k) => Date.parse(k.recibido_en) || 0,
    ciudad: (k) => (k.ciudad ?? "").toLowerCase(),
    etapa: (k) => ordenColumna(k.columna),
  };
  const f = clave[e.orden];
  return items
    .filter(
      (k) =>
        (!e.fVer || k.veredicto === e.fVer) &&
        (!e.fEta || k.etapa === e.fEta) &&
        (corte === null || (Date.parse(k.recibido_en) || 0) >= corte),
    )
    .sort((a, z) => {
      const A = f(a),
        Z = f(z);
      return (A < Z ? -1 : A > Z ? 1 : 0) * e.dir || masRecientesPrimero(a, z);
    });
}

/** Tablero: columna por columna (en el orden de COLUMNAS), y en cada una los más recientes primero. */
export function ordenTablero(items: CandidatoPortal[], pestana: Pestana): CandidatoPortal[] {
  const cols = COLUMNAS_DE[pestana];
  return items
    .filter((k) => cols.includes(k.columna))
    .sort((a, z) => cols.indexOf(a.columna) - cols.indexOf(z.columna) || masRecientesPrimero(a, z));
}

/** Tarjetas: los más recientes primero. */
export const ordenTarjetas = (items: CandidatoPortal[]) => items.slice().sort(masRecientesPrimero);

/** El orden exacto en que la vista muestra a sus candidatos (con la búsqueda, si hay). */
export function ordenDeVista(
  items: CandidatoPortal[],
  vista: Vista,
  pestana: Pestana,
  lista: EstadoLista,
  q = "",
  /** Plaza, etapa o «solo nuevos»: lo que la vista filtra además de la pestaña y la búsqueda. */
  filtro: (k: CandidatoPortal) => boolean = () => true,
): CandidatoPortal[] {
  const visibles = items.filter((k) => enPestana(k, pestana) && filtro(k) && coincide(k, q));
  if (vista === "lista") return filasLista(visibles, lista);
  if (vista === "tarjetas") return ordenTarjetas(visibles);
  return ordenTablero(visibles, pestana);
}

// ---------------------------------------------------------------- sessionStorage (con try/catch: puede no haber)
export type Secuencia = {
  ids: string[];
  /** Liga para regresar a la vista de donde vino. */
  volver: string;
  /** Cómo se llama esa vista, en palabras («Activos · Tablero»). */
  etiqueta: string;
};
const CLAVE_SEC = "portal-secuencia";
const CLAVE_LISTA = "portal-lista";

function leer<T>(clave: string): T | null {
  try {
    const t = sessionStorage.getItem(clave);
    return t ? (JSON.parse(t) as T) : null;
  } catch {
    return null;
  }
}
function guardar(clave: string, valor: unknown) {
  try {
    sessionStorage.setItem(clave, JSON.stringify(valor));
  } catch {
    /* sin almacenamiento: la ficha la reconstruye */
  }
}

export const leerSecuencia = () => {
  const s = leer<Secuencia>(CLAVE_SEC);
  return s && Array.isArray(s.ids) && typeof s.volver === "string" ? s : null;
};
export const guardarSecuencia = (s: Secuencia) => guardar(CLAVE_SEC, s);

export const leerEstadoLista = (): EstadoLista => ({ ...LISTA_INICIAL, ...(leer<Partial<EstadoLista>>(CLAVE_LISTA) ?? {}) });
export const guardarEstadoLista = (e: EstadoLista) => guardar(CLAVE_LISTA, e);

/** Posición de `id` en la secuencia: «3 de 28», anterior y siguiente (null en los extremos). */
export function posicion(ids: string[], id: string) {
  const i = ids.indexOf(id);
  if (i < 0) return null;
  return { n: i + 1, total: ids.length, anterior: ids[i - 1] ?? null, siguiente: ids[i + 1] ?? null };
}
